import BaseApiService from "./BaseApiService";

/**
 * Settings API Service
 *
 * Handles all user settings and preferences API calls including:
 * - Language preferences
 * - Password changes
 * - Notification settings
 * - Account preferences
 * - Profile updates
 *
 * @class SettingsApiService
 * @extends BaseApiService
 */
class SettingsApiService extends BaseApiService {
  /**
   * Updates user's language preference
   * @param {string} language - Language code ('en', 'de', etc.)
   * @returns {Promise<Object>} Update confirmation
   */
  async updateLanguage(language) {
    return await this.post("/settings/update/language", { language });
  }

  /**
   * Updates user's password
   * @param {string} currentPassword - Current password
   * @param {string} password - New password
   * @returns {Promise<Object>} Password update confirmation
   */
  async updatePassword(currentPassword, password) {
    return await this.post("/settings/update/password", {
      currentPassword,
      password,
    });
  }

  /**
   * Gets user's current settings and preferences
   * @returns {Promise<Object>} User settings data
   */
  async getUserSettings() {
    return await this.get("/settings");
  }

  /**
   * Updates user's notification preferences
   * @param {Object} notificationSettings - Notification preferences
   * @param {boolean} notificationSettings.emailNotifications - Enable email notifications
   * @param {boolean} notificationSettings.pushNotifications - Enable push notifications
   * @param {string} notificationSettings.notificationTime - Preferred notification time
   * @param {Array<string>} notificationSettings.notificationDays - Preferred days for notifications
   * @returns {Promise<Object>} Update confirmation
   */
  async updateNotificationSettings(notificationSettings) {
    return await this.put("/settings/notifications", notificationSettings);
  }

  /**
   * Updates user's exercise preferences
   * @param {Object} exercisePreferences - Exercise settings
   * @param {Array<number>} exercisePreferences.preferredExerciseTypes - Preferred exercise type IDs
   * @param {number} exercisePreferences.exerciseDuration - Preferred exercise duration in minutes
   * @param {string} exercisePreferences.difficultyLevel - Difficulty level ('beginner', 'intermediate', 'advanced')
   * @returns {Promise<Object>} Update confirmation
   */
  async updateExercisePreferences(exercisePreferences) {
    return await this.put("/settings/exercises", exercisePreferences);
  }

  /**
   * Updates user's profile information
   * @param {Object} profileData - Profile data to update
   * @param {string} [profileData.name] - User's full name
   * @param {string} [profileData.email] - User's email address
   * @param {string} [profileData.phoneNumber] - User's phone number
   * @param {string} [profileData.dateOfBirth] - User's date of birth
   * @param {string} [profileData.gender] - User's gender
   * @returns {Promise<Object>} Update confirmation
   */
  async updateProfile(profileData) {
    return await this.put("/settings/profile", profileData);
  }

  /**
   * Updates user's privacy settings
   * @param {Object} privacySettings - Privacy preferences
   * @param {boolean} privacySettings.shareUsageData - Allow usage data sharing
   * @param {boolean} privacySettings.shareExerciseData - Allow exercise data sharing
   * @param {boolean} privacySettings.allowAnalytics - Allow analytics tracking
   * @returns {Promise<Object>} Update confirmation
   */
  async updatePrivacySettings(privacySettings) {
    return await this.put("/settings/privacy", privacySettings);
  }

  /**
   * Gets user's subscription information
   * @returns {Promise<Object>} Subscription details
   */
  async getSubscriptionInfo() {
    return await this.get("/settings/subscription");
  }

  /**
   * Updates subscription preferences
   * @param {Object} subscriptionData - Subscription settings
   * @param {string} subscriptionData.plan - Subscription plan type
   * @param {boolean} subscriptionData.autoRenew - Auto-renewal preference
   * @returns {Promise<Object>} Update confirmation
   */
  async updateSubscription(subscriptionData) {
    return await this.put("/settings/subscription", subscriptionData);
  }

  /**
   * Gets user's data export
   * @param {string} [format='json'] - Export format ('json', 'csv', 'pdf')
   * @returns {Promise<Blob|Object>} Exported data
   */
  async exportUserData(format = "json") {
    const response = await this.axiosInstance.get("/settings/export", {
      params: { format },
      responseType: format === "json" ? "json" : "blob",
    });
    return response.data;
  }

  /**
   * Requests account deletion
   * @param {string} reason - Reason for account deletion
   * @param {string} password - User's password for confirmation
   * @returns {Promise<Object>} Deletion request confirmation
   */
  async requestAccountDeletion(reason, password) {
    return await this.post("/settings/delete-account", { reason, password });
  }

  /**
   * Cancels account deletion request
   * @returns {Promise<Object>} Cancellation confirmation
   */
  async cancelAccountDeletion() {
    return await this.post("/settings/cancel-deletion");
  }

  /**
   * Updates user's timezone
   * @param {string} timezone - Timezone identifier (e.g., 'Europe/Berlin', 'America/New_York')
   * @returns {Promise<Object>} Update confirmation
   */
  async updateTimezone(timezone) {
    return await this.post("/settings/update/timezone", { timezone });
  }

  /**
   * Gets available timezones
   * @returns {Promise<Array>} List of available timezones
   */
  async getAvailableTimezones() {
    return await this.get("/settings/timezones");
  }

  /**
   * Updates user's theme preference
   * @param {string} theme - Theme preference ('light', 'dark', 'auto')
   * @returns {Promise<Object>} Update confirmation
   */
  async updateTheme(theme) {
    return await this.post("/settings/update/theme", { theme });
  }

  /**
   * Gets user's activity and usage statistics
   * @param {string} [period='month'] - Period for statistics ('week', 'month', 'year')
   * @returns {Promise<Object>} Usage statistics
   */
  async getUsageStatistics(period = "month") {
    return await this.get(`/settings/statistics?period=${period}`);
  }

  /**
   * Updates user's accessibility preferences
   * @param {Object} accessibilitySettings - Accessibility settings
   * @param {boolean} accessibilitySettings.highContrast - Enable high contrast mode
   * @param {string} accessibilitySettings.fontSize - Font size preference ('small', 'medium', 'large')
   * @param {boolean} accessibilitySettings.reduceMotion - Reduce animations and motion
   * @param {boolean} accessibilitySettings.screenReader - Screen reader optimization
   * @returns {Promise<Object>} Update confirmation
   */
  async updateAccessibilitySettings(accessibilitySettings) {
    return await this.put("/settings/accessibility", accessibilitySettings);
  }

  /**
   * Gets user's connected devices and sessions
   * @returns {Promise<Array>} List of active sessions and devices
   */
  async getActiveSessions() {
    return await this.get("/settings/sessions");
  }

  /**
   * Terminates a specific session
   * @param {string} sessionId - ID of the session to terminate
   * @returns {Promise<Object>} Termination confirmation
   */
  async terminateSession(sessionId) {
    return await this.delete(`/settings/sessions/${sessionId}`);
  }

  /**
   * Terminates all other sessions except current
   * @returns {Promise<Object>} Termination confirmation
   */
  async terminateAllOtherSessions() {
    return await this.delete("/settings/sessions/others");
  }

  // ========================================
  // Movement & Exercise Configuration
  // ========================================

  /**
   * Updates movement/standing reminder configuration
   * @param {Object} movementConfig - Movement settings
   * @param {number} movementConfig.startTime - Start time as decimal (9.5 = 9:30)
   * @param {number} movementConfig.endTime - End time as decimal
   * @param {number} movementConfig.frequency - Frequency in hours
   * @param {Array<number>} movementConfig.daysOfWeek - Days of week (1-7)
   * @param {boolean} movementConfig.isEnabled - Whether reminders are enabled
   * @param {string} movementConfig.offset - Timezone offset (e.g., "+02")
   * @returns {Promise<Object>} Update confirmation
   */
  async updateMovementConfig(movementConfig) {
    return await this.post("/stand/update/config", {
      startTime: movementConfig.startTime,
      endTime: movementConfig.endTime,
      frequency: movementConfig.frequency,
      daysOfWeek: movementConfig.daysOfWeek,
      isEnabled: movementConfig.isEnabled,
      offset: movementConfig.offset,
    });
  }

  /**
   * Updates exercise reminder configuration
   * @param {Object} exerciseConfig - Exercise settings
   * @param {number} exerciseConfig.startTime - Start time as decimal
   * @param {number} exerciseConfig.endTime - End time as decimal
   * @param {number} exerciseConfig.frequency - Frequency in hours
   * @param {Array<number>} exerciseConfig.daysOfWeek - Days of week (1-7)
   * @param {boolean} exerciseConfig.isEnabled - Whether reminders are enabled
   * @param {Array<number>} exerciseConfig.preferences - Exercise preference IDs
   * @param {boolean} exerciseConfig.isPhysioEnabled - Whether physio exercises are enabled
   * @param {string} exerciseConfig.offset - Timezone offset
   * @returns {Promise<Object>} Update confirmation
   */
  async updateExerciseConfig(exerciseConfig) {
    return await this.post(
      "/excercise/update/config",
      {
        startTime: exerciseConfig.startTime,
        endTime: exerciseConfig.endTime,
        frequency: exerciseConfig.frequency,
        daysOfWeek: exerciseConfig.daysOfWeek,
        isEnabled: exerciseConfig.isEnabled,
        preferences: exerciseConfig.preferences,
        isPhysioEnabled: exerciseConfig.isPhysioEnabled,
        offset: exerciseConfig.offset,
      },
      {
        timeout: 15000, 
      },
    );
  }

  /**
   * Snoozes exercise notifications
   * @returns {Promise<Object>} Snooze confirmation
   */
  async snoozeNotifications() {
    return await this.post("/excercise/snooze", {});
  }

  /**
   * Unsnoozes exercise notifications
   * @returns {Promise<Object>} Unsnooze confirmation
   */
  async unsnoozeNotifications() {
    return await this.post("/excercise/unsnooze", {});
  }

  /**
   * Checks snooze status for today
   * @returns {Promise<boolean>} true if snoozed
   */
  async areNotificationsSnoozed() {
    const res = await this.get("/excercise/isSnoozed");
    return !!res?.enabled;
  }

  /**
   * Gets complete user settings including movement and exercise config
   * @returns {Promise<Object>} Complete settings data
   */
  async getCompleteSettings() {
    return await this.get("/settings");
  }

  /**
   * Utility: Converts HH:MM time string to decimal hours
   * @param {string} timeString - Time in HH:MM format
   * @returns {number} Time as decimal (e.g., 9.5 for 9:30)
   */
  timeStringToDecimal(timeString) {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours + minutes / 60;
  }

  /**
   * Utility: Converts decimal hours to HH:MM time string
   * @param {number} decimalTime - Time as decimal hours
   * @returns {string} Time in HH:MM format
   */
  decimalToTimeString(decimalTime) {
    const hours = Math.floor(decimalTime);
    const minutes = Math.round((decimalTime - hours) * 60);
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  /**
   * Utility: Gets formatted timezone offset
   * @returns {string} Timezone offset in format "+HH" or "-HH"
   */
  getFormattedTimezoneOffset() {
    const offsetMinutes = new Date().getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetSign = offsetMinutes <= 0 ? "+" : "-";
    return `${offsetSign}${offsetHours.toString().padStart(2, "0")}`;
  }
}

// Create and export a singleton instance
const settingsApiService = new SettingsApiService();
export default settingsApiService;
